import React, { useEffect, useMemo, useState } from "react";
import { api, dateFR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Pencil, Trash2, Search, ClipboardList, CheckCircle2, XCircle, MessageSquarePlus, StickyNote } from "lucide-react";
import { toast } from "sonner";

const empty = {
  client_id: "",
  client_nom: "",
  client_telephone: "",
  marque: "",
  modele: "",
  annee_min: "",
  annee_max: "",
  km_max: "",
  budget_max: "",
  couleur: "",
  petite_note: "",
  notes: "",
  statut: "active",
};

const STATUTS = [
  { key: "active", label: "Active", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  { key: "cloturee", label: "Clôturée", cls: "bg-slate-100 text-slate-700 border-slate-300" },
  { key: "annulee", label: "Annulée", cls: "bg-red-50 text-red-700 border-red-200" },
];

function StatusBadge({ statut }) {
  const cfg = STATUTS.find((s) => s.key === statut) || STATUTS[0];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-bold border ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

function fmtMoney(n) {
  if (n == null || n === "") return "—";
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Number(n) || 0);
}

export default function Commandes() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [filterStatut, setFilterStatut] = useState("active");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  // Suivi (timeline) dialog
  const [suiviOpen, setSuiviOpen] = useState(false);
  const [suiviTarget, setSuiviTarget] = useState(null);
  const [newNote, setNewNote] = useState("");
  const [suiviLoading, setSuiviLoading] = useState(false);

  const openSuivi = async (c) => {
    setSuiviTarget(c);
    setNewNote("");
    setSuiviOpen(true);
  };

  const refreshSuivi = async () => {
    if (!suiviTarget) return;
    try {
      const { data } = await api.get(`/commandes/${suiviTarget.id}`);
      setSuiviTarget(data);
      // Refresh the table so notes counter stays accurate
      setItems((prev) => prev.map((x) => (x.id === data.id ? data : x)));
    } catch {/* ignore */}
  };

  const addNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;
    setSuiviLoading(true);
    try {
      await api.post(`/commandes/${suiviTarget.id}/notes`, { text: newNote.trim() });
      setNewNote("");
      await refreshSuivi();
      toast.success("Note ajoutée");
    } catch {
      toast.error("Erreur");
    }
    setSuiviLoading(false);
  };

  const removeNote = async (noteId) => {
    if (!window.confirm("Supprimer cette note ?")) return;
    try {
      await api.delete(`/commandes/${suiviTarget.id}/notes/${noteId}`);
      await refreshSuivi();
    } catch {
      toast.error("Erreur");
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/commandes");
      setItems(data);
      const c = await api.get("/clients");
      setClients(c.data || []);
    } catch (err) {
      toast.error("Erreur de chargement");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return items.filter((c) => {
      if (filterStatut !== "all" && c.statut !== filterStatut) return false;
      if (!t) return true;
      return (
        (c.client_nom || "").toLowerCase().includes(t) ||
        (c.marque || "").toLowerCase().includes(t) ||
        (c.modele || "").toLowerCase().includes(t) ||
        (c.notes || "").toLowerCase().includes(t)
      );
    });
  }, [items, q, filterStatut]);

  const onPickClient = (id) => {
    const c = clients.find((x) => x.id === id);
    if (c) {
      setForm((f) => ({
        ...f,
        client_id: c.id,
        client_nom: c.nom || "",
        client_telephone: c.telephone || "",
      }));
    } else {
      setForm((f) => ({ ...f, client_id: "" }));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_nom?.trim()) {
      toast.error("Le nom du client est obligatoire");
      return;
    }
    const payload = {
      ...form,
      annee_min: form.annee_min ? Number(form.annee_min) : null,
      annee_max: form.annee_max ? Number(form.annee_max) : null,
      km_max: form.km_max ? Number(form.km_max) : null,
      budget_max: form.budget_max ? Number(form.budget_max) : null,
    };
    try {
      if (editing) {
        await api.put(`/commandes/${editing.id}`, payload);
        toast.success("Commande mise à jour");
      } else {
        await api.post("/commandes", payload);
        toast.success("Commande créée");
      }
      setOpen(false);
      setEditing(null);
      setForm(empty);
      load();
    } catch (err) {
      const msg = err.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Erreur");
    }
  };

  const startEdit = (c) => {
    setEditing(c);
    setForm({
      client_id: c.client_id || "",
      client_nom: c.client_nom || "",
      client_telephone: c.client_telephone || "",
      marque: c.marque || "",
      modele: c.modele || "",
      annee_min: c.annee_min ?? "",
      annee_max: c.annee_max ?? "",
      km_max: c.km_max ?? "",
      budget_max: c.budget_max ?? "",
      couleur: c.couleur || "",
      petite_note: c.petite_note || "",
      notes: c.notes || "",
      statut: c.statut || "active",
    });
    setOpen(true);
  };

  const changeStatut = async (c, statut) => {
    try {
      await api.put(`/commandes/${c.id}`, { statut });
      toast.success(`Commande ${statut === "cloturee" ? "clôturée" : statut === "annulee" ? "annulée" : "réactivée"}`);
      load();
    } catch {
      toast.error("Erreur");
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Supprimer définitivement la commande de ${c.client_nom} ?`)) return;
    try {
      await api.delete(`/commandes/${c.id}`);
      toast.success("Supprimée");
      load();
    } catch {
      toast.error("Erreur");
    }
  };

  return (
    <div className="space-y-6" data-testid="commandes-page">
      <header className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground font-semibold">Recherche véhicule</div>
          <h1 className="font-display font-black text-4xl tracking-tighter mt-1 flex items-center gap-3">
            <ClipboardList size={28} className="text-klein" />
            Commandes Client
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Suivez les véhicules recherchés par vos clients. Clôturez la commande une fois la vente conclue.
          </p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(empty); } }}>
          <DialogTrigger asChild>
            <Button data-testid="commande-new" className="bg-klein hover:bg-blue-800 text-white rounded-sm">
              <Plus size={14} className="mr-1" /> Nouvelle commande
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-display tracking-tight">
                {editing ? "Modifier la commande" : "Nouvelle commande client"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <Label className="text-xs uppercase tracking-wider">Client existant (facultatif)</Label>
                  <select
                    className="mt-1 w-full h-10 rounded-sm border border-input bg-background px-3 text-sm"
                    data-testid="commande-client-select"
                    value={form.client_id || ""}
                    onChange={(e) => onPickClient(e.target.value)}
                  >
                    <option value="">— Saisir manuellement —</option>
                    {clients.map((c) => (
                      <option key={c.id} value={c.id}>{c.nom} {c.telephone ? `· ${c.telephone}` : ""}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Nom du client *</Label>
                  <Input
                    data-testid="commande-client-nom"
                    required
                    value={form.client_nom}
                    onChange={(e) => setForm({ ...form, client_nom: e.target.value })}
                    className="mt-1 rounded-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Téléphone</Label>
                  <Input
                    data-testid="commande-client-tel"
                    value={form.client_telephone}
                    onChange={(e) => setForm({ ...form, client_telephone: e.target.value })}
                    className="mt-1 rounded-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Marque souhaitée</Label>
                  <Input value={form.marque} onChange={(e) => setForm({ ...form, marque: e.target.value })} className="mt-1 rounded-sm" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Modèle souhaité</Label>
                  <Input value={form.modele} onChange={(e) => setForm({ ...form, modele: e.target.value })} className="mt-1 rounded-sm" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Année min.</Label>
                  <Input type="number" inputMode="decimal" value={form.annee_min} onChange={(e) => setForm({ ...form, annee_min: e.target.value })} className="mt-1 rounded-sm" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Année max.</Label>
                  <Input type="number" inputMode="decimal" value={form.annee_max} onChange={(e) => setForm({ ...form, annee_max: e.target.value })} className="mt-1 rounded-sm" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Km max.</Label>
                  <Input type="number" inputMode="decimal" value={form.km_max} onChange={(e) => setForm({ ...form, km_max: e.target.value })} className="mt-1 rounded-sm" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Budget max. (€)</Label>
                  <Input type="number" inputMode="decimal" value={form.budget_max} onChange={(e) => setForm({ ...form, budget_max: e.target.value })} className="mt-1 rounded-sm" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Couleur</Label>
                  <Input value={form.couleur} onChange={(e) => setForm({ ...form, couleur: e.target.value })} className="mt-1 rounded-sm" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Statut</Label>
                  <select
                    className="mt-1 w-full h-10 rounded-sm border border-input bg-background px-3 text-sm"
                    value={form.statut}
                    onChange={(e) => setForm({ ...form, statut: e.target.value })}
                  >
                    {STATUTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider flex items-center gap-1.5">
                  <StickyNote size={12} className="text-amber-600" /> Petite note (affichée en jaune sur la ligne)
                </Label>
                <Input
                  data-testid="commande-petite-note"
                  maxLength={120}
                  placeholder="Ex : Rappeler jeudi · Acompte versé · Voiture à voir samedi…"
                  value={form.petite_note}
                  onChange={(e) => setForm({ ...form, petite_note: e.target.value })}
                  className="mt-1 rounded-sm"
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  Max. 120 caractères · Idéal pour un rappel rapide visible en un coup d'œil.
                </p>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Notes / Vœux du client</Label>
                <Textarea
                  rows={4}
                  placeholder="Ex : SUV familial avec attelage, boîte automatique, intérieur cuir, jamais accidenté…"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="mt-1 rounded-sm"
                />
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="commande-submit" className="bg-klein hover:bg-blue-800 text-white rounded-sm">
                  {editing ? "Mettre à jour" : "Créer"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </header>

      {/* Filtres */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="flex gap-2 flex-wrap">
          <button
            data-testid="filter-all"
            onClick={() => setFilterStatut("all")}
            className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-sm border ${filterStatut === "all" ? "bg-klein text-white border-klein" : "border-border hover:bg-slate-50"}`}
          >
            Toutes · {items.length}
          </button>
          {STATUTS.map((s) => {
            const count = items.filter((i) => i.statut === s.key).length;
            return (
              <button
                key={s.key}
                data-testid={`filter-${s.key}`}
                onClick={() => setFilterStatut(s.key)}
                className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-sm border ${filterStatut === s.key ? "bg-klein text-white border-klein" : "border-border hover:bg-slate-50"}`}
              >
                {s.label} · {count}
              </button>
            );
          })}
        </div>
        <div className="relative max-w-xs w-full">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="commande-search"
            type="search"
            name="commande-search"
            autoComplete="off"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher…"
            className="pl-9 rounded-sm"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-card border border-border rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-6 py-3">Date</th>
              <th className="px-6 py-3">Client</th>
              <th className="px-6 py-3">Recherche</th>
              <th className="px-6 py-3">Année</th>
              <th className="px-6 py-3 text-right">Km max.</th>
              <th className="px-6 py-3 text-right">Budget</th>
              <th className="px-6 py-3 text-center">Statut</th>
              <th className="px-6 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="px-6 py-10 text-center text-muted-foreground">Chargement…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={8} className="px-6 py-10 text-center text-muted-foreground">Aucune commande</td></tr>
            ) : filtered.map((c) => (
              <tr key={c.id} className="border-t hover:bg-slate-50" data-testid={`commande-row-${c.id}`}>
                <td className="px-6 py-3 whitespace-nowrap text-xs">{dateFR(c.date_commande)}</td>
                <td className="px-6 py-3">
                  <div className="font-medium">{c.client_nom}</div>
                  {c.client_telephone && <div className="text-xs text-muted-foreground">{c.client_telephone}</div>}
                </td>
                <td className="px-6 py-3">
                  <div className="font-semibold">{[c.marque, c.modele].filter(Boolean).join(" ") || "—"}</div>
                  {c.couleur && <div className="text-xs text-muted-foreground">{c.couleur}</div>}
                  {c.petite_note && (
                    <div className="mt-1 inline-flex items-start gap-1.5 px-2 py-1 bg-amber-50 border border-amber-200 rounded-sm text-[11px] font-medium text-amber-900 max-w-md" data-testid={`commande-petite-note-${c.id}`}>
                      <StickyNote size={12} className="shrink-0 mt-0.5" />
                      <span className="whitespace-pre-wrap break-words">{c.petite_note}</span>
                    </div>
                  )}
                  {c.notes && <div className="text-xs text-muted-foreground mt-1 line-clamp-2 max-w-md italic">« {c.notes} »</div>}
                </td>
                <td className="px-6 py-3 text-xs whitespace-nowrap">
                  {c.annee_min || c.annee_max ? `${c.annee_min || ""}${c.annee_min && c.annee_max ? " – " : ""}${c.annee_max || ""}` : "—"}
                </td>
                <td className="px-6 py-3 text-right text-xs whitespace-nowrap">
                  {c.km_max ? `${Number(c.km_max).toLocaleString("fr-FR")} km` : "—"}
                </td>
                <td className="px-6 py-3 text-right font-mono text-xs">{fmtMoney(c.budget_max)}</td>
                <td className="px-6 py-3 text-center"><StatusBadge statut={c.statut} /></td>
                <td className="px-6 py-3 text-right">
                  <div className="inline-flex items-center gap-1 flex-wrap justify-end">
                    {c.statut === "active" && (
                      <>
                        <button
                          data-testid={`commande-close-${c.id}`}
                          onClick={() => changeStatut(c, "cloturee")}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs text-emerald-700 hover:bg-emerald-50 rounded-sm"
                          title="Clôturer (vente trouvée)"
                        >
                          <CheckCircle2 size={12} /> Clôturer
                        </button>
                        <button
                          data-testid={`commande-cancel-${c.id}`}
                          onClick={() => changeStatut(c, "annulee")}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs text-orange-700 hover:bg-orange-50 rounded-sm"
                          title="Annuler"
                        >
                          <XCircle size={12} /> Annuler
                        </button>
                      </>
                    )}
                    {c.statut !== "active" && (
                      <button
                        data-testid={`commande-reactivate-${c.id}`}
                        onClick={() => changeStatut(c, "active")}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs text-klein hover:bg-klein/10 rounded-sm"
                        title="Réactiver"
                      >
                        Réactiver
                      </button>
                    )}
                    <button
                      data-testid={`commande-suivi-${c.id}`}
                      onClick={() => openSuivi(c)}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs text-klein hover:bg-klein/10 rounded-sm"
                      title="Historique de suivi"
                    >
                      <MessageSquarePlus size={12} /> Suivi
                      {Array.isArray(c.historique) && c.historique.length > 0 && (
                        <span className="ml-1 inline-flex items-center justify-center min-w-4 h-4 px-1 text-[10px] bg-klein text-white rounded-full">
                          {c.historique.length}
                        </span>
                      )}
                    </button>
                    <button
                      data-testid={`commande-edit-${c.id}`}
                      onClick={() => startEdit(c)}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs hover:bg-slate-100 rounded-sm"
                    >
                      <Pencil size={12} /> Modifier
                    </button>
                    <button
                      data-testid={`commande-delete-${c.id}`}
                      onClick={() => remove(c)}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs text-destructive hover:bg-red-50 rounded-sm"
                    >
                      <Trash2 size={12} /> Supprimer
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Timeline / Suivi dialog */}
      <Dialog open={suiviOpen} onOpenChange={setSuiviOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight flex items-center gap-2">
              <MessageSquarePlus size={18} className="text-klein" />
              Suivi de la commande
            </DialogTitle>
          </DialogHeader>
          {suiviTarget && (
            <div className="space-y-4">
              <div className="text-sm">
                <div className="font-semibold">{suiviTarget.client_nom}</div>
                <div className="text-xs text-muted-foreground">
                  {[suiviTarget.marque, suiviTarget.modele].filter(Boolean).join(" ") || "Aucun véhicule précisé"}
                </div>
                {suiviTarget.petite_note && (
                  <div className="mt-2 inline-flex items-start gap-1.5 px-2 py-1 bg-amber-50 border border-amber-200 rounded-sm text-[11px] font-medium text-amber-900">
                    <StickyNote size={12} className="shrink-0 mt-0.5" />
                    <span>{suiviTarget.petite_note}</span>
                  </div>
                )}
              </div>

              {/* Add new note */}
              <form onSubmit={addNote} className="space-y-2">
                <Label className="text-xs uppercase tracking-wider">Ajouter une note</Label>
                <Textarea
                  data-testid="suivi-new-note"
                  rows={2}
                  placeholder="Ex : Client a rappelé, attend une réponse demain matin"
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  className="rounded-sm"
                />
                <div className="flex justify-end">
                  <Button
                    type="submit"
                    disabled={suiviLoading || !newNote.trim()}
                    data-testid="suivi-add-submit"
                    className="bg-klein hover:bg-blue-800 text-white rounded-sm"
                  >
                    {suiviLoading ? "Ajout…" : "Ajouter la note"}
                  </Button>
                </div>
              </form>

              {/* Timeline */}
              <div>
                <div className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground font-semibold mb-2">
                  Historique
                </div>
                {(!suiviTarget.historique || suiviTarget.historique.length === 0) ? (
                  <div className="text-sm text-muted-foreground italic py-4 text-center border border-dashed border-border rounded-sm">
                    Aucune note pour l'instant.
                  </div>
                ) : (
                  <ol className="space-y-2 max-h-80 overflow-y-auto pr-1">
                    {[...suiviTarget.historique].reverse().map((h) => (
                      <li key={h.id} className="border-l-2 border-klein/40 pl-3 py-1 group">
                        <div className="text-[11px] text-muted-foreground flex items-center justify-between gap-2">
                          <span>
                            {new Date(h.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
                            {h.author ? ` · ${h.author}` : ""}
                          </span>
                          <button
                            onClick={() => removeNote(h.id)}
                            className="opacity-0 group-hover:opacity-100 text-destructive hover:underline"
                            title="Supprimer cette note"
                          >
                            supprimer
                          </button>
                        </div>
                        <div className="text-sm whitespace-pre-wrap mt-0.5">{h.text}</div>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
